<template>
    <section class="flex flex-col items-stretch mb-4 px-4 md:px-8 text-zinc-900 dark:text-zinc-100">
        <div class="flex flex-wrap justify-center gap-4 w-full">
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="from-date">Desde</label>
                <Calendar id="from-date" v-model="fromDate" showIcon :showOnFocus="false" class="w-full" dateFormat="dd/mm/yy" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="to-date">Hasta</label>
                <Calendar id="to-date" v-model="toDate" showIcon :showOnFocus="false" class="w-full" dateFormat="dd/mm/yy" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="company">Empresa</label>
                <Dropdown id="company" v-model="selectedCompany" :options="companies" optionLabel="name" placeholder="Selecciona una empresa"
                class="w-full" @change="companyChange" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="branch">Sucursal</label>
                <Dropdown id="branch" v-model="selectedBranch" :options="selectedCompany.branches" optionLabel="name" placeholder="Selecciona una sucursal"
                class="w-full" :disabled="formDisabled" filter/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="opacity-0" for="show-btn">Acción</label>
                <Button id="show-btn" label="Mostrar" @click="fillTable" :disabled="buttonDisabled"></Button>
            </div>
        </div>
    </section>
    <div class="mt-4 mb-4">
        <p><i class="pi pi-exclamation-triangle text-yellow-300"></i> El seleccionar una fecha muy amplia puede crear inestabilidad en el sistema, se recomienda precaución.</p>
    </div>
    <section class="grid justify-center px-4 md:px-8 text-zinc-900 dark:text-zinc-100" v-show="invoices.length > 0">
        <div>
            <h2 class="text-left">Análisis de facturas</h2>
        </div>
        <div>
            <h3 class="text-left">{{ subtitle }}</h3>
        </div>
        <div class="card overflow-auto text-zinc-900 dark:text-zinc-100">
            <div class="flex flex-wrap justify-between items-center gap-4 mb-4">
                <div class="flex flex-wrap gap-4">
                    <div class="flex gap-2 items-center">
                        <div class="w-4 h-4 bg-green-500 rounded"></div>
                        <span>Pagada</span>
                    </div>
                    <div class="flex gap-2 items-center">
                        <div class="w-4 h-4 bg-orange-400 rounded"></div>
                        <span>Pago parcial</span>
                    </div>
                    <div class="flex gap-2 items-center">
                        <div class="w-4 h-4 bg-red-500 rounded"></div>
                        <span>Sin pagar</span>
                    </div>
                </div>
                <div class="bg-zinc-200 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 rounded-lg px-4 py-2 flex flex-wrap items-end shadow" v-show="invoices.length > 0">
                    <span class="text-sm mb-2 font-semibold self-center px-2">Ordenar por:</span>
                    <div class="flex items-center gap-2">
                        <Button icon="pi pi-money-bill" text rounded value="total" class="order-table-btn"
                        @click="sortTables('total')" v-tooltip.bottom="'Total'"/>
                        <Button icon="pi pi-sort-alpha-down" text rounded value="name" class="order-table-btn"
                        @click="sortTables('name')" v-tooltip.bottom="'Razón social'"/>
                        <Button icon="pi pi-calendar" text rounded value="period" class="order-table-btn"
                        @click="sortTables('period')" v-tooltip.bottom="'Emisión'" />
                    </div>
                </div>
            </div>
            <DataTable
                :value="invoices"
                stripedRows
                showGridlines
                tableStyle="min-width: 30rem"
                class="min-w-full invoices-table"
                scrollable
                scrollHeight="525px">
                <Column field="serial" header="Folio">
                    <template #body="{data}">
                        <span
                            :class="{'green-cell': data.level === 0,
                                'orange-cell': data.level === 1,
                                'red-cell': data.level === 2}">
                            {{ data.serial }}
                        </span>
                    </template>
                </Column>
                <Column field="period" header="Emisión" :bodyStyle="{ minWidth: '120px'}" :headerStyle="{ minWidth: '120px' }"></Column>
                <Column field="name" header="Razón Social"></Column>
                <Column field="subtotal" header="Subtotal"></Column>
                <Column field="tax" header="IVA"></Column>
                <Column field="total" header="Total"></Column>
            </DataTable>
        </div>
    </section>
</template>

<script>
    import axiosInstance from '../axiosInstance';

    export default {
        data() {
            return {
                fromDate: new Date(),
                toDate: new Date(),
                selectedCompany: {},
                companies: [],
                selectedBranch: {},
                invoices: [],
                subtitle: "",
                buttonDisabled: false,
                formDisabled: false
            }
        },
        methods: {
            /**
             * Fetch initial data for invoice analysis (companies, default selections, date range)
             */
            async fetchInvoiceInitialData() {
                this.$emit("update-loading", true);
                this.buttonDisabled = true;
                this.formDisabled = true;
                let data = {};
                try {
                    const response = axiosInstance.post("/api/invoices_viewer", {
                        token: localStorage.getItem('token')
                    });

                    data = await response;
            
                } catch(error) {
                    console.error("fetchInvoiceInitialData: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                }

                return data;
            },

            /**
             * Set initial data for the component (companies, branches, date range, default selections)
             */
            setInitialData() {
                this.fetchInvoiceInitialData().then((response) => {
                    if(response.status === 200){
                        const { from: fromDate, to: toDate, defaultCmp, companies } = response.data;
                        this.fromDate = this.obtainDateType(fromDate);
                        this.toDate = this.obtainDateType(toDate);

                        this.companies = companies.map((company) => {
                            return {
                                name: company.data.name,
                                id: company.data.id,
                                branches: company.branches
                            }
                        });

                        this.selectedCompany = {
                            name: defaultCmp.data.name, 
                            id: defaultCmp.data.id, 
                            branches: defaultCmp.branches
                        }

                        this.selectedBranch = this.selectedCompany.branches[0];
                        this.buttonDisabled = false;
                        this.formDisabled = false;
                    } else {
                        console.warn("setInitialData: There was a problem with the request.", response);
                        this.$toast.add({ severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                    }
                })
            },

            /**
             * Fetch invoice data based on selected filters (date range, company, branch) and update the table
             */
            async fetchInvoicesData() {
                this.$emit("update-loading", true);
                this.buttonDisabled = true;
                let data = {};
                try {
                    const response = axiosInstance.post("/api/invoices_viewer_data", {
                        token: localStorage.getItem('token'),
                        from: this.formatDate(this.fromDate),
                        to: this.formatDate(this.toDate),
                        company_id: this.selectedCompany.id,
                        branch_id: this.selectedBranch.id
                    });

                    data = await response;
            
                } catch(error) {
                    console.error("fetchInvoicesData: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                    this.buttonDisabled = false;
                }

                return data;
            },

            /**
             * Fill the table with invoice data based on selected filters (date range, company, branch)
             * and set the subtitle for the report
             */
            fillTable(){
                this.fetchInvoicesData().then((response) => {
                    if(response.status == 200) {
                        const { invoices, subtotal, tax, total, subtitle } = response.data;

                        this.invoices = Object.values(invoices).map(({invoice_period, level, name, serial_str, subtotal, tax, total}) => {
                            return {
                                period: invoice_period,
                                level,
                                name,
                                serial: serial_str,
                                subtotal,
                                tax,
                                total
                            }
                        });

                        const totalData = {
                            serial: "",
                            period: "",
                            name: "",
                            subtotal,
                            tax,
                            total
                        }

                        this.invoices.push(totalData);
                        this.subtitle = subtitle;
                    } else {
                        console.warn("fillTable: There was a problem with the request.", response);
                        this.$toast.add({ severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                    }
                })
            },

            sortTables(criterion){
                this.sortBy = criterion;
                if (this.invoices.length < 2) return;

                const invoicesWithoutTotals = this.invoices.slice(0, -1);
                const totals = this.invoices[this.invoices.length - 1];

                let sorted = [...invoicesWithoutTotals];
                if (criterion === 'total') {
                    sorted.sort((a, b) => {
                        const aNum = parseFloat((a.total || '0').toString().replace(/,/g, ''));
                        const bNum = parseFloat((b.total || '0').toString().replace(/,/g, ''));
                        return bNum - aNum;
                    });
                } else if (criterion === 'name') {
                    sorted.sort((a, b) => a.name.trim().localeCompare(b.name.trim()));
                } else if (criterion === 'period') {
                    sorted.sort((a, b) => {
                        const aDate = new Date(a.period);
                        const bDate = new Date(b.period);
                        return bDate - aDate;
                    });
                }

                this.invoices = [...sorted, totals];
            },

            obtainDateType(date){
                const [dateY, dateM, dateD] = date.split("-");
                return new Date(Number(dateY), Number(dateM) - 1, Number(dateD))
            },

            formatDate(date){
                const month = ((date.getMonth()+1).toString()).padStart(2, "0");
                const day = ((date.getDate()).toString()).padStart(2, "0");
                return `${date.getFullYear()}-${month}-${day}`
            },

            companyChange(){
                this.selectedBranch = this.selectedCompany.branches[0];
            }
        },
        mounted() {
            this.setInitialData();
        }
    }
</script>

<style scoped>
::v-deep(.p-datatable) {
    border-radius: 0.5rem !important;
    overflow: hidden !important;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1) !important;
}

::v-deep(.p-datatable tbody tr td) {
    text-align: right !important;
}

::v-deep(.invoices-table.p-datatable .p-datatable-thead > tr > th) {
    background-color: #0f1338 !important;
    color: white !important;
}

::v-deep(.invoices-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(odd) > td) {
    background-color: #f3f4f6 !important;
    color: #111827 !important;
}

::v-deep(.invoices-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(even) > td) {
    background-color: #ffffff !important;
    color: #111827 !important;
}

::v-deep(.invoices-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.green-cell)) {
    /* background-color: #dcfce7 !important; */
    color: #166534 !important;
    font-weight: 600 !important;
}

::v-deep(.invoices-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.orange-cell)) {
    /* background-color: #fed7aa !important; */
    color: #d66400 !important;
    font-weight: 600 !important;
}

::v-deep(.invoices-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.red-cell)) {
    /* background-color: #fecaca !important; */
    color: #ab0000 !important;
    font-weight: 600 !important;
}

::v-deep(.invoices-table.p-datatable .p-datatable-tbody > tr:last-child > td) {
    background-color: #0f1338 !important;
    color: white !important;
    font-weight: 600 !important;
}

::v-deep(.p-calendar .p-button) {
    @apply bg-zinc-200 text-zinc-900 border-zinc-300 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600 transition-colors;
}

::v-deep(.p-calendar .p-button:hover) {
    @apply bg-zinc-300 dark:bg-zinc-600;
}

#show-btn {
    @apply bg-zinc-200 text-zinc-900 border border-zinc-300 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600 rounded px-4 py-2 text-lg shadow focus:outline-none transition-colors;
}

Button.order-table-btn {
    @apply bg-zinc-200 text-zinc-900 border-zinc-300 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600 transition-colors;
}
</style>

<style>
.dark .invoices-table.p-datatable .p-datatable-thead > tr > th {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .invoices-table.p-datatable .p-datatable-tbody > tr > td {
    color: white !important;
}

.dark .invoices-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(odd) > td {
    background-color: #18181b !important;
    color: white !important;
}

.dark .invoices-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(even) > td {
    background-color: #27272a !important;
    color: white !important;
}

.dark .invoices-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.green-cell) {
    /* background-color: #166534 !important; */
    color: #9bffbe !important;
    font-weight: 600 !important;
}

.dark .invoices-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.orange-cell) {
    /* background-color: #9a3412 !important; */
    color: #ffc17b !important;
    font-weight: 600 !important;
}

.dark .invoices-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.red-cell) {
    /* background-color: #991b1b !important; */
    color: #ff7070 !important;
    font-weight: 600 !important;
}

.dark .invoices-table.p-datatable .p-datatable-tbody > tr:last-child > td {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}
</style>